import React, { useState, useEffect } from 'react';
import {
  Box,
  Switch,
  FormControlLabel,
  Typography,
  CircularProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import authorityService from '../../services/AuthorityService';
import authService from '../../services/AuthService';

/**
 * CompactAuthorityToggle - Slim version for main components
 * Just shows toggle and status without detailed explanations
 */
const CompactAuthorityToggle = ({ onAuthorityChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [authorityStatus, setAuthorityStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Get current user
  useEffect(() => {
    const user = authService.getCurrentUser();
    const testUser = user || { username: "cpzitrone" };
    
    setCurrentUser(testUser);
    if (testUser?.username) {
      checkAuthorityStatus();
    } else {
      setLoading(false);
    }
  }, []);

  // Check authority status
  const checkAuthorityStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const status = await authorityService.checkAuthorityStatus();
      setAuthorityStatus(status);
      
      // Notify parent component
      if (onAuthorityChange) {
        onAuthorityChange(status);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle authority toggle
  const handleAuthorityToggle = async (grant) => {
    try {
      setProcessing(true);
      setError(null);

      if (grant) {
        await authorityService.grantAuthority();
      } else {
        await authorityService.revokeAuthority();
      }

      // Refresh status after a delay
      setTimeout(() => {
        checkAuthorityStatus();
      }, 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Get status icon
  const getStatusIcon = () => {
    if (loading || processing) return <CircularProgress size={16} />;
    if (!authorityStatus) return <SecurityIcon sx={{ fontSize: 16 }} />;
    
    return authorityStatus.hasAuthority 
      ? <CheckCircleIcon sx={{ color: colors.greenAccent[400], fontSize: 16 }} />
      : <ErrorIcon sx={{ color: colors.redAccent[400], fontSize: 16 }} />;
  };

  if (!currentUser) {
    return null; // Don't show anything if no user
  }

  return (
    <Box 
      display="flex" 
      alignItems="center" 
      gap={2}
      p={1.5}
      bgcolor={colors.primary[500]}
      borderRadius={1}
      border={`1px solid ${colors.primary[300]}`}
      mb={2}
    >
      {/* Status Icon */}
      <Box display="flex" alignItems="center">
        {getStatusIcon()}
      </Box>

      {/* Authority Toggle */}
      <Tooltip 
        title={authorityStatus?.hasAuthority 
          ? "Authority enabled - SplTrader can execute bids automatically" 
          : "Authority disabled - Enable to use automated bidding"
        }
        arrow
      >
        <FormControlLabel
          control={
            <Switch
              checked={authorityStatus?.hasAuthority || false}
              onChange={(e) => handleAuthorityToggle(e.target.checked)}
              disabled={processing || loading}
              color="secondary"
              size="small"
            />
          }
          label={
            <Typography variant="body2" fontWeight="500">
              Authority {authorityStatus?.hasAuthority ? "Enabled" : "Disabled"}
            </Typography>
          }
          labelPlacement="end"
          sx={{ m: 0 }}
        />
      </Tooltip>

      {/* Error state (compact) */}
      {error && !loading && (
        <Tooltip title={error} arrow>
          <ErrorIcon sx={{ color: colors.redAccent[400], fontSize: 16 }} />
        </Tooltip>
      )}
    </Box>
  );
};

export default CompactAuthorityToggle;
